import { useLayoutEffect, useState } from 'react'
import { ClozeAnswerAudio, type Block } from '@w/content-blocks'
import { IconButton } from '@w/ui/components/icon-button'
import { WandIcon } from '@w/ui/components/icon'
import { clozePosition, useEditorModeValue, useAnswerAutoFocus, useClozeHintFail, useAnswerPractice, useAnswerReveal, usePracticeControls } from '../../editor-mode-ctx'
import { useEditor, useEditorState } from '../../editor-state'
import { useMediaUrl } from '../../media-url-ctx'
import { TrainingTextInput } from '../../training-text-input'
import { serializeBlocksMd } from '../../editable-block-utils'
import type { BlockView } from '../view-api'
import { children as blockChildren, plain } from '../../core/tree-read'
import { QuestionSettings } from './question-settings'

export const ClozeView: BlockView = ({ block, api, children }) => {
  const mode = useEditorModeValue()
  useLayoutEffect(() => {
    if (api.mode !== 'edit') return () => {}
    const findParent = (items: Block[]): Block | undefined => {
      for (const item of items) {
        if (blockChildren(item).some(child => child.id === block.id)) return item
        const found = findParent(blockChildren(item))
        if (found) return found
      }
      return undefined
    }
    const parent = findParent(api.state.blocks)
    const element = api.root?.querySelector<HTMLElement>(`[data-block-id="${block.id}"]`)?.closest<HTMLElement>('.cb-paragraph')
    if (!element || parent?.type !== 'p') return () => {}
    if (parent.children.filter(child => child.type !== 'cloze').every(child => !plain(child))) element.dataset.cbFieldPlaceholder = api.t('contentBlocksEditor.placeholders.note')
    else delete element.dataset.cbFieldPlaceholder
    return () => { delete element.dataset.cbFieldPlaceholder }
  }, [api, block.id])
  if (block.type !== 'cloze') return null
  if (api.mode !== 'answer') return <><span data-block-id={block.id} data-flashcard-qid={block.qid}
    data-cloze="true" className="bg-surface-2 dark:bg-surface-3 box-decoration-clone rounded-lg outline-none focus-within:ring-1 focus-within:ring-primary"
    data-cb-field-placeholder={api.mode === 'edit' && !plain(block) ? api.t('contentBlocksEditor.placeholders.cloze') : undefined}>
    {children}
  </span>{api.mode === 'edit' ? <span contentEditable={false}><QuestionSettings block={block} api={api} compact /></span> : null}</>
  const position = clozePosition(mode, block.qid)
  return <span data-block-id={block.id} data-flashcard-qid={block.qid} contentEditable={false}>
    {position === 'before' ? serializeBlocksMd(block.children)
      : position === 'after' ? <span data-testid="flashcards-training-cloze-hidden">{api.t('contentBlocksEditor.document.hiddenCloze')}</span>
        : <ClozeAnswer key={block.id} block={block} api={api} />}
  </span>
}

function ClozeAnswer({ block, api }: { block: Extract<Block, { type: 'cloze' }>; api: Parameters<BlockView>[0]['api'] }) {
  const autoFocus = useAnswerAutoFocus()
  const resolve = useMediaUrl()
  const editor = useEditor()
  const answer = useEditorState(state => state.answers[block.id] ?? {})
  const onFail = useClozeHintFail()
  const practice = useAnswerPractice()
  const controls = usePracticeControls(block)
  const { showAnswer, mark } = useAnswerReveal(block)
  const [hintLength, setHintLength] = useState(1)
  const answerText = serializeBlocksMd(block.children)
  const chars = Array.from(answerText)
  if (showAnswer) return <>
    <ClozeAnswerAudio block={block} resolve={resolve} />
    <span className="bg-primary text-on-primary data-[answer-result=correct]:bg-surface-success data-[answer-result=correct]:text-on-surface-success data-[answer-result=wrong]:bg-surface-error data-[answer-result=wrong]:text-on-surface-error mx-0.5 rounded-lg px-1.5 py-0.5"
      data-answer-result={answerResult(mark)} data-testid="flashcards-training-cloze-answer">{answerText}</span>
    {block.tip && <span data-testid="flashcards-training-cloze-explanation" className="text-on-surface-secondary ms-2 text-sm">{block.tip}</span>}
    {controls}
  </>
  // The blank, its hint and, under host practice, its question's controls sit inline in that order, as in training.
  return <span className="mx-0.5 inline-flex items-baseline gap-2" data-cloze-blank="">
    <TrainingTextInput aria-label={api.t('contentBlocksEditor.placeholders.cloze')}
      // oxlint-disable-next-line eslint-plugin-jsx-a11y/no-autofocus -- active cloze is the training task's focus target
      autoFocus={autoFocus}
      // The blank reads in the sentence's own size, underlined; its hit area grows to at least 44px tall without changing its look.
      minimal className="border-outline inline-flex w-auto min-w-[3em] border-0 border-b-2 border-solid align-baseline text-[length:inherit] leading-[inherit] [&>input]:-inset-y-3 [&>input]:h-auto"
      data-testid="flashcards-training-cloze-input" value={answer.text ?? ''}
      placeholder={`${chars.slice(0, hintLength).join('')}...`}
      onEnter={practice ? () => { practice.submit(block.qid, 'enter') } : undefined}
      onValueChange={text => { editor.setAnswer(block.id, { text }) }} />
    {onFail && hintLength < chars.length && <IconButton className="size-5" data-testid="flashcards-training-cloze-hint"
      semanticRole="primary" size="sm" type="button" variant="text"
      onMouseDown={event => { event.preventDefault() }} onClick={() => {
        if (Math.random() < 0.5) setHintLength(length => length + 1)
        else onFail(block.qid)
      }}><WandIcon />Reveal next letter or fail</IconButton>}
    {controls}
  </span>
}

export function answerResult(mark?: number): 'correct' | 'wrong' | undefined {
  return mark === 1 ? 'correct' : mark !== undefined && mark >= 0 ? 'wrong' : undefined
}
